import { CardGuide } from '../types';
import { supabase } from '../config/supabase';
import { standardizeCardName } from './cardService';

interface InterpretationResponse {
  interpretation: string;
}

export async function getCardInterpretation(cardName: string, isReversed: boolean): Promise<InterpretationResponse | null> {
  try {
    const standardizedName = standardizeCardName(cardName);
    
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/interpret-card`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        cardName: standardizedName,
        isReversed
      })
    });

    if (!response.ok) {
      throw new Error('Failed to get card interpretation');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting card interpretation:', error);
    return null;
  }
}

export const getCardGuide = async (name: string, isReversed: boolean): Promise<CardGuide | null> => {
  try {
    const standardizedName = standardizeCardName(name);
    const interpretation = await getCardInterpretation(standardizedName, isReversed);
    
    if (!interpretation) {
      throw new Error('Failed to get interpretation');
    }

    // Split the response into sections
    const sections = interpretation.interpretation.split('\n\n');
    
    let cardInterpretation = '';
    let arcanaInfo = '';
    let imageryInfo = '';
    let currentSection = '';
    
    for (const section of sections) {
      const sectionLower = section.toLowerCase();
      
      // Detect section headers
      if (sectionLower.includes('card interpretation')) {
        currentSection = 'interpretation';
        continue;
      } else if (sectionLower.includes('arcana type')) {
        currentSection = 'arcana';
        continue;
      } else if (sectionLower.includes('traditional imagery')) {
        currentSection = 'imagery';
        continue;
      }

      // Add content to appropriate section
      if (currentSection === 'interpretation') {
        cardInterpretation += (cardInterpretation ? '\n\n' : '') + section;
      } else if (currentSection === 'arcana') {
        arcanaInfo += (arcanaInfo ? '\n\n' : '') + section;
      } else if (currentSection === 'imagery') {
        imageryInfo += (imageryInfo ? '\n\n' : '') + section;
      }
    }

    // Determine if it's Major or Minor Arcana and get suit
    const isMajor = arcanaInfo.toLowerCase().includes('major arcana') ||
                   ['fool', 'magician', 'priestess', 'empress', 'emperor', 'hierophant', 
                    'lovers', 'chariot', 'strength', 'hermit', 'justice', 'hanged', 
                    'death', 'temperance', 'devil', 'tower', 'star', 'moon', 'sun', 
                    'judgement', 'world'].some(card => 
                     standardizedName.toLowerCase().includes(card));

    // Extract suit for Minor Arcana
    const suitMatch = arcanaInfo.match(/suit of (\w+)/i);
    const suit = suitMatch ? suitMatch[1].toLowerCase() : '';

    return {
      name: standardizedName,
      type: isMajor ? 'major' : 'minor',
      upright: cardInterpretation.trim(),
      reversed: cardInterpretation.trim(),
      imagery: imageryInfo.trim(),
      suit,
      arcanaInfo: arcanaInfo.trim()
    };
  } catch (error) {
    console.error('Error in getCardGuide:', error);
    return null;
  }
};

export const getAllCards = (): CardGuide[] => {
  // Return a list of common card names for autocomplete
  return [
    { name: 'The Fool', type: 'major', upright: '', reversed: '', imagery: '', suit: '', arcanaInfo: '' },
    { name: 'The Magician', type: 'major', upright: '', reversed: '', imagery: '', suit: '', arcanaInfo: '' },
    { name: 'The High Priestess', type: 'major', upright: '', reversed: '', imagery: '', suit: '', arcanaInfo: '' },
    { name: 'The Empress', type: 'major', upright: '', reversed: '', imagery: '', suit: '', arcanaInfo: '' },
    { name: 'The Emperor', type: 'major', upright: '', reversed: '', imagery: '', suit: '', arcanaInfo: '' },
    // Add more cards as needed for autocomplete
  ];
};

export const getSpreadGuide = (spreadType: string): { name: string; description: string; positions: { name: string; meaning: string }[] } => {
  const spreads = {
    '1-card': {
      name: 'Single Card Reading',
      description: 'A simple but powerful reading focusing on a single card to provide insight into your question.',
      positions: [
        { name: 'Daily Card', meaning: 'Represents the guidance and energy for your day' }
      ]
    },
    'virtual-daily-card': {
      name: 'Daily Card Draw',
      description: 'A simple but powerful reading focusing on a single card to provide insight into your day.',
      positions: [
        { name: 'Daily Card', meaning: 'Represents the guidance and energy for your day' }
      ]
    },
    '3-card': {
      name: 'Three Card Spread',
      description: 'A versatile spread that can represent past-present-future, mind-body-spirit, or situation-action-outcome.',
      positions: [
        { name: 'First Position', meaning: 'Past influence or current situation' },
        { name: 'Second Position', meaning: 'Present circumstances or action to take' },
        { name: 'Third Position', meaning: 'Future outcome or potential result' }
      ]
    },
    '5-card': {
      name: 'Five Card Cross',
      description: 'A detailed spread examining different aspects of your situation.',
      positions: [
        { name: 'Center', meaning: 'The core issue or present situation' },
        { name: 'Above', meaning: 'What crowns or influences the situation' },
        { name: 'Below', meaning: 'The foundation or underlying factors' },
        { name: 'Left', meaning: 'Past influences or what\'s behind you' },
        { name: 'Right', meaning: 'Future potential or what lies ahead' }
      ]
    },
    'celtic-cross': {
      name: 'Celtic Cross',
      description: 'A comprehensive spread providing deep insight into your situation and its various influences.',
      positions: [
        { name: 'Present', meaning: 'The current situation or focus' },
        { name: 'Challenge', meaning: 'What crosses or challenges you' },
        { name: 'Foundation', meaning: 'The basis of the situation' },
        { name: 'Past', meaning: 'Recent past influences' },
        { name: 'Crown', meaning: 'Potential outcome' },
        { name: 'Future', meaning: 'Near future developments' },
        { name: 'Self', meaning: 'Your attitude or approach' },
        { name: 'Environment', meaning: 'External influences' },
        { name: 'Hopes/Fears', meaning: 'Your inner emotions' },
        { name: 'Outcome', meaning: 'The final outcome' }
      ]
    },
    'two-options': {
      name: 'Two Options Spread',
      description: 'A specialized spread to help you choose between two different paths or decisions.',
      positions: [
        { name: 'What Grounds You', meaning: 'Your core values and foundation' },
        { name: 'Your Key Motivation', meaning: 'What drives you in this decision' },
        { name: 'Your Ideal Outcome', meaning: 'What you hope to achieve' },
        { name: 'About Option 1', meaning: 'What you need to know about the first path' },
        { name: 'Option 1 Outcome', meaning: 'Likely result of choosing Option 1' },
        { name: 'About Option 2', meaning: 'What you need to know about the second path' },
        { name: 'Option 2 Outcome', meaning: 'Likely result of choosing Option 2' }
      ]
    },
    'relationship': {
      name: 'Relationship Spread',
      description: 'A focused spread exploring the dynamics, challenges, and potential of a relationship.',
      positions: [
        { name: 'You', meaning: 'Your role and energy in the relationship' },
        { name: 'Partner', meaning: 'Your partner\'s role and energy' },
        { name: 'Relationship Dynamic', meaning: 'The current state of the relationship' },
        { name: 'Challenges', meaning: 'Obstacles or issues to address' },
        { name: 'Strengths', meaning: 'What supports and strengthens the bond' },
        { name: 'Potential', meaning: 'Where the relationship could grow' },
        { name: 'Advice', meaning: 'Guidance for nurturing the relationship' }
      ]
    }
  };

  return spreads[spreadType as keyof typeof spreads] || spreads['3-card'];
};